/**
 * ProtectedContent — wraps any block of paid course content with a
 * layered anti-piracy shell:
 *
 *   1. `user-select: none` on the wrapper + all descendants so drag-select
 *      is disabled. Clean, native platform behaviour — no JS involved.
 *   2. Interception of `copy`, `cut`, `contextmenu`, and `dragstart` events.
 *      A friendly toast tells the user why + fires-and-forgets a report
 *      to `/api/content-protection/report-attempt` for admin visibility.
 *   3. Diagonal watermark overlay stamped with the current user's email
 *      + timestamp. The overlay uses `pointer-events: none` so it doesn't
 *      break clicks or scrolling but IS captured by any screenshot.
 *   4. Optional focus-blur — if `blurOnBlur` is true, the content blurs
 *      when the browser window loses focus (deters screen-recording
 *      tools from grabbing the pane while the user alt-tabs).
 *   5. Keyboard shortcut blocker for `Ctrl/Cmd+C`, `Ctrl/Cmd+P`, and
 *      `Ctrl/Cmd+S` while the wrapped block has focus.
 *
 * NOTE: This is DRM-lite, not perfect. A determined attacker with dev
 * tools can bypass everything. The real revenue-side protection is the
 * refund-window drip gate on the server. This layer exists to deter
 * casual copying and to make any leaked screenshots traceable.
 */

import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fire-and-forget attempt reporter. Any failure is swallowed so it can
 * never break the lesson experience.
 */
const reportAttempt = async ({ lessonId, courseId, kind }) => {
  try {
    const token = window.localStorage.getItem('token');
    if (!token) return;
    await axios.post(
      `${API_BASE}/api/content-protection/report-attempt`,
      { lesson_id: lessonId, course_id: courseId, kind },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (_) {
    /* silent */
  }
};

const ProtectedContent = ({
  children,
  watermark,          // { email, issued_at }
  lessonId,
  courseId,
  blurOnBlur = true,
  className = '',
  testId = 'protected-content',
}) => {
  const rootRef = useRef(null);

  // Block copy / cut / drag / right-click via native event listeners on the
  // wrapper — cheaper than react synthetic-event props and impossible to
  // bypass with `stopPropagation` in a nested child.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const stop = (kind) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      toast.warning('Copying course content is disabled — see our Terms of Use.', {
        id: 'content-protection-block',
      });
      reportAttempt({ lessonId, courseId, kind });
    };

    const onCopy = stop('copy');
    const onCut = stop('cut');
    const onDragStart = stop('drag');
    const onContext = stop('right_click');

    el.addEventListener('copy', onCopy);
    el.addEventListener('cut', onCut);
    el.addEventListener('dragstart', onDragStart);
    el.addEventListener('contextmenu', onContext);

    return () => {
      el.removeEventListener('copy', onCopy);
      el.removeEventListener('cut', onCut);
      el.removeEventListener('dragstart', onDragStart);
      el.removeEventListener('contextmenu', onContext);
    };
  }, [lessonId, courseId]);

  // Global keyboard shortcut blocker — active whenever this component is
  // mounted. We only interfere with keystrokes that would exfiltrate the
  // content; regular typing (arrow keys, quiz inputs) is untouched.
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      const key = (e.key || '').toLowerCase();
      // ⌘/Ctrl + C is only blocked when the wrapped tree is the copy source.
      if (key === 'p' || key === 's') {
        e.preventDefault();
        toast.warning('Printing / saving course content is disabled.');
        reportAttempt({ lessonId, courseId, kind: key === 'p' ? 'print' : 'save' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lessonId, courseId]);

  // Blur content when window loses focus.
  useEffect(() => {
    if (!blurOnBlur) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const onBlur = () => { el.style.filter = 'blur(8px)'; };
    const onFocus = () => { el.style.filter = 'none'; };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [blurOnBlur]);

  const wmLabel = watermark
    ? `${watermark.email || ''} · ${new Date(watermark.issued_at || Date.now()).toLocaleString()}`
    : '';

  return (
    <div
      ref={rootRef}
      data-testid={testId}
      className={`relative select-none ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        transition: 'filter 200ms ease',
      }}
    >
      {children}

      {/* Diagonal watermark tile. Uses SVG so it scales cleanly and can't be
          easily removed by adjusting a single CSS variable. */}
      {watermark?.email && (
        <div
          aria-hidden
          data-testid="content-watermark"
          className="pointer-events-none absolute inset-0 overflow-hidden z-10"
          style={{ opacity: 0.11 }}
        >
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="fda-wm" width="360" height="220" patternUnits="userSpaceOnUse" patternTransform="rotate(-24)">
                <text
                  x="0"
                  y="40"
                  fontFamily="ui-sans-serif, system-ui, sans-serif"
                  fontSize="14"
                  fontWeight="700"
                  fill="currentColor"
                >
                  {wmLabel}
                </text>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#fda-wm)" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default ProtectedContent;
